import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import {
    Bell, MessageCircle, Heart, Share2, UserPlus, Loader2,
    ShieldCheck, AlertCircle, Sparkles, Info,
} from 'lucide-react';
import ReseauLayout from '../../components/reseau/ReseauLayout';

const TYPE_META = {
    post_reaction: { icon: Heart, color: 'text-pink-500', label: 'a réagi à votre publication' },
    post_comment: { icon: MessageCircle, color: 'text-blue-500', label: 'a commenté votre publication' },
    post_comment_reply: { icon: MessageCircle, color: 'text-blue-500', label: 'a répondu à un commentaire' },
    post_shared: { icon: Share2, color: 'text-emerald-500', label: 'a partagé votre publication' },
    publication_from_followed_author: { icon: Bell, color: 'text-[#681793]', label: 'Nouvelle publication d\'un professionnel suivi' },
    publication_from_followed_agency: { icon: Bell, color: 'text-[#681793]', label: 'Nouvelle publication d\'une agence suivie' },
    new_follower: { icon: UserPlus, color: 'text-emerald-500', label: 'vous suit désormais' },
    proof_proposed: { icon: ShieldCheck, color: 'text-[#681793]', label: 'Une nouvelle preuve professionnelle attend votre examen' },
    new_contact_request: { icon: AlertCircle, color: 'text-[#FF914D]', label: 'Nouvelle demande vendeur' },
    new_review: { icon: AlertCircle, color: 'text-[#FF914D]', label: 'Un avis vendeur attend votre réponse' },
    verification_available: { icon: ShieldCheck, color: 'text-[#681793]', label: 'Vérification disponible' },
    profile_shared: { icon: Share2, color: 'text-emerald-500', label: 'Votre profil a été partagé' },
    profile_followed: { icon: UserPlus, color: 'text-emerald-500', label: 'Nouvel abonné sur votre profil' },
    profile_viewed_milestone: { icon: Bell, color: 'text-[#FF914D]', label: 'Votre profil atteint un nouveau palier de vues' },
    agency_followed: { icon: UserPlus, color: 'text-emerald-500', label: 'Nouvel abonné à votre agence' },
    agency_team_update: { icon: Bell, color: 'text-[#681793]', label: 'Mise à jour de votre équipe' },
    completeness_reminder: { icon: Info, color: 'text-slate-400', label: 'Votre profil peut être enrichi' },
    seller_intent_received: { icon: AlertCircle, color: 'text-[#FF914D]', label: 'Nouvelle demande vendeur reçue' },
    seller_intent_assigned: { icon: AlertCircle, color: 'text-[#FF914D]', label: 'Une demande vous a été affectée' },
    seller_review_received: { icon: AlertCircle, color: 'text-[#FF914D]', label: 'Un avis vendeur attend votre réponse' },
};

const PROOF_TYPE_LABEL = {
    homly_seniority: "Ancienneté sur Homly",
    identity_verified: "Identité confirmée",
    email_verified: "E-mail vérifié",
    agency_affiliation: "Affiliation à votre agence",
    active_zones_declared: "Zones d'intervention",
    posts_local_recent: "Publications locales récentes",
};
const PROOF_CATEGORY_LABEL = {
    identity: "Identité", experience: "Expérience", local: "Implantation locale",
    activity: "Activité", quality: "Qualité",
};

const CATEGORY_DEFINITION = {
    action_required: {
        label: 'Action requise',
        sublabel: 'À traiter rapidement.',
        icon: AlertCircle,
        accent: 'var(--reseau-orange,#FF914D)',
    },
    activity: {
        label: 'Activité utile',
        sublabel: 'Ce qui se passe autour de votre activité.',
        icon: Sparkles,
        accent: 'var(--reseau-violet,#681793)',
    },
    info: {
        label: 'Informations',
        sublabel: 'Mises à jour utiles, à votre rythme.',
        icon: Info,
        accent: 'var(--reseau-ink-muted,#7A6F84)',
    },
};

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function NotifItem({ n, onRead }) {
    const meta = TYPE_META[n.type] || { icon: Bell, color: 'text-slate-400', label: n.type };
    const Icon = meta.icon;
    const actorName = n.actor?.display_name || '';
    const initials = n.actor?.initials || '?';
    const postTitle = n.target_post?.title;
    const isProof = n.type === 'proof_proposed';
    const proofCtx = n.context || {};
    const proofTypeLbl = PROOF_TYPE_LABEL[proofCtx.proof_type] || proofCtx.proof_type;
    const proofCatLbl = PROOF_CATEGORY_LABEL[proofCtx.category] || proofCtx.category;

    // Mapping type → route directe
    const routeFor = (() => {
        if (n.link) return n.link;
        if (isProof) return `/reseau/ma-reputation?focus=${proofCtx.proof_id || ''}`;
        if (n.type === 'new_contact_request') return '/reseau/demandes-vendeurs';
        if (n.type === 'new_review') return '/reseau/avis-vendeurs';
        if (n.type === 'completeness_reminder') return '/reseau/cockpit';
        if (n.type?.startsWith('post_')) return `/reseau/fil`;
        return null;
    })();

    const inner = (
        <>
            {n.actor ? (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex-shrink-0
                                flex items-center justify-center text-white text-xs font-medium">
                    {initials}
                </div>
            ) : (
                <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${meta.color}`} />
            )}
            <div className="flex-1 min-w-0">
                <p className="text-sm" style={{ color: 'var(--reseau-ink)' }}>
                    {actorName && <span className="font-semibold">{actorName} </span>}
                    <span style={{ color: 'var(--reseau-ink-muted)' }}>{meta.label}</span>
                    {postTitle && (
                        <span className="italic" style={{ color: 'var(--reseau-ink-faint)' }}> · {postTitle}</span>
                    )}
                </p>
                {isProof && proofTypeLbl && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--reseau-ink-muted)' }}
                       data-testid={`notif-proof-meta-${n.id}`}>
                        <span className="font-medium">{proofTypeLbl}</span>
                        {proofCatLbl && <span style={{ color: 'var(--reseau-ink-faint)' }}> · {proofCatLbl}</span>}
                    </p>
                )}
                <p className="text-xs mt-1" style={{ color: 'var(--reseau-ink-faint)' }}>
                    {formatDate(n.created_at)}
                </p>
            </div>
            {!n.read_at && (
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 flex-shrink-0"
                      style={{ background: 'rgba(255,145,77,0.12)', color: '#B95E1A' }}
                      data-testid={`notif-unread-${n.id}`}>
                    Non lue
                </span>
            )}
        </>
    );

    const baseCls = `w-full text-left rounded-xl p-3 sm:p-4 mb-2 flex items-start gap-3 transition ${
        !n.read_at
            ? 'bg-white border border-slate-200'
            : 'bg-white/60 border border-transparent'
    }`;

    if (routeFor) {
        return (
            <Link to={routeFor}
                  data-testid={`notif-${n.id}`}
                  onClick={() => !n.read_at && onRead(n.id)}
                  className={baseCls + ' hover:shadow-sm'}>
                {inner}
            </Link>
        );
    }
    return (
        <button data-testid={`notif-${n.id}`}
                onClick={() => !n.read_at && onRead(n.id)}
                className={baseCls}>
            {inner}
        </button>
    );
}

function CategorySection({ catKey, items, onRead }) {
    const def = CATEGORY_DEFINITION[catKey];
    if (!def) return null;
    const Icon = def.icon;
    if (items.length === 0) return null;
    const unread = items.filter(n => !n.read_at).length;
    return (
        <section className="mb-7" data-testid={`notif-cat-${catKey}`}>
            <header className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center"
                          style={{ background: `${def.accent}1A`, color: def.accent }}>
                        <Icon className="w-4 h-4" />
                    </span>
                    <div>
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                            {def.label}
                            {unread > 0 && (
                                <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded-full"
                                      style={{ background: `${def.accent}1A`, color: def.accent }}>
                                    {unread} non lue{unread > 1 ? 's' : ''}
                                </span>
                            )}
                        </h3>
                        <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                            {def.sublabel}
                        </p>
                    </div>
                </div>
            </header>
            {items.map(n => <NotifItem key={n.id} n={n} onRead={onRead} />)}
        </section>
    );
}

export default function ReseauNotificationsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState('all'); // all | action_required | activity | info
    const [counts, setCounts] = useState({});

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/reseau/notifications?limit=50');
            setItems(r.data.items || []);
            setCounts(r.data.category_counts || {});
        } catch { setItems([]); }
        finally { setLoading(false); }
    };

    const markRead = async (id) => {
        await api.post(`/reseau/notifications/${id}/read`).catch(() => {});
        setItems(arr => arr.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    };

    const markAllRead = async () => {
        await api.post('/reseau/notifications/read-all').catch(() => {});
        setItems(arr => arr.map(n => ({ ...n, read_at: new Date().toISOString() })));
    };

    const grouped = useMemo(() => {
        const buckets = { action_required: [], activity: [], info: [] };
        items.forEach(n => {
            const c = n.category && buckets[n.category] ? n.category : 'info';
            buckets[c].push(n);
        });
        return buckets;
    }, [items]);

    const hasAnyUnread = items.some(n => !n.read_at);
    const filteredView = activeCat === 'all' ? null : grouped[activeCat] || [];

    const CATEGORY_ORDER = ['action_required', 'activity', 'info'];

    return (
        <ReseauLayout headerTitle="Notifications"
                      headerSubtitle="Action requise, activité utile, informations — séparées pour vous faire gagner du temps.">
            <main className="mx-auto px-4 sm:px-6 py-6" style={{ maxWidth: 'var(--reseau-maxw-card)' }}>
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="notif-filter-bar">
                    {[
                        { key: 'all', label: 'Toutes', total: items.length },
                        { key: 'action_required', label: 'Action requise', total: counts.action_required?.total || 0 },
                        { key: 'activity', label: 'Activité', total: counts.activity?.total || 0 },
                        { key: 'info', label: 'Infos', total: counts.info?.total || 0 },
                    ].map(t => (
                        <button key={t.key}
                                onClick={() => setActiveCat(t.key)}
                                data-testid={`notif-filter-${t.key}`}
                                className={`text-sm px-3 py-1.5 rounded-full transition border ${
                                    activeCat === t.key
                                        ? 'bg-[#681793] text-white border-[#681793]'
                                        : 'bg-white border-slate-200 text-slate-600 hover:border-[#681793]/30'
                                }`}>
                            {t.label} {t.total > 0 && <span className="opacity-70">· {t.total}</span>}
                        </button>
                    ))}
                    <div className="flex-1" />
                    {hasAnyUnread && (
                        <button onClick={markAllRead}
                                data-testid="mark-all-read-btn"
                                className="text-sm hover:underline"
                                style={{ color: 'var(--reseau-violet)' }}>
                            Tout marquer comme lu
                        </button>
                    )}
                </div>

                {loading && (
                    <div className="text-center py-8" data-testid="notif-loading">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <div className="reseau-empty" data-testid="notif-empty">
                        <Bell className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--reseau-ink-faint)' }} />
                        <h3>Aucune notification pour l&apos;instant</h3>
                        <p>
                            Publiez un premier contenu, ouvrez votre profil public ou invitez un vendeur à laisser un avis :
                            les retours apparaîtront ici, classés par utilité.
                        </p>
                        <Link to="/reseau/cockpit"
                              data-testid="empty-notif-cta"
                              className="reseau-btn reseau-btn-primary mt-4">
                            Voir mon cockpit
                        </Link>
                    </div>
                )}

                {!loading && items.length > 0 && (
                    activeCat === 'all' ? (
                        CATEGORY_ORDER.map(catKey => (
                            <CategorySection key={catKey}
                                             catKey={catKey}
                                             items={grouped[catKey]}
                                             onRead={markRead} />
                        ))
                    ) : (
                        filteredView.length === 0 ? (
                            <div className="reseau-empty" data-testid={`notif-empty-${activeCat}`}>
                                <p>Aucune notification dans cette catégorie.</p>
                            </div>
                        ) : (
                            filteredView.map(n => <NotifItem key={n.id} n={n} onRead={markRead} />)
                        )
                    )
                )}
            </main>
        </ReseauLayout>
    );
}
